import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "settings-section" }
const _hoisted_2 = { class: "container" }
const _hoisted_3 = {
  key: 2,
  class: "settings"
}
const _hoisted_4 = { class: "content" }
const _hoisted_5 = { class: "settings__section settings__section--notifications" }
const _hoisted_6 = {
  key: 0,
  class: "notifications-disabled"
}
const _hoisted_7 = { class: "content__buttons" }
const _hoisted_8 = { class: "content__buttons__row" }
const _hoisted_9 = { class: "settings__toggle-title" }
const _hoisted_10 = {
  key: 0,
  class: "settings__toggle-subtitle"
}
const _hoisted_11 = {
  key: 0,
  class: "content__buttons__row"
}
const _hoisted_12 = { class: "settings__toggle-title" }
const _hoisted_13 = { class: "settings__toggle-subtitle" }
const _hoisted_14 = { class: "settings__section settings__section--email" }
const _hoisted_15 = { class: "settings__section-subtitle email-address__field" }
const _hoisted_16 = {
  key: 0,
  class: "update-password__label"
}
const _hoisted_17 = {
  key: 1,
  class: "update-email__label"
}
const _hoisted_18 = { class: "settings__section settings__section--delete-account" }

import { onBeforeMount, onBeforeUnmount, ref } from 'vue'

import RootPageLayout from '@/app/components/RootPageLayout.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'
import InfoDeleteAccount from '@/app/components/settings/InfoDeleteAccount.vue'
import ChangePasswordPopup from '@/app/components/settings/ChangePasswordPopup.vue'
import ChangeEmailPopup from '@/app/components/settings/ChangeEmailPopup.vue'

import { SettingsView } from '@/models/settings'
import { getEmail } from '@/services/auth'
import { returnToValue19051Variant } from '@/services/ab'
import { openNativeAppSettings } from '@/services/capacitor.appSettingsPlugin'
import { AppState } from '@capacitor/app'
import { emitter } from '@/services/mitt'
import { platform } from '@/services/platform'
import { notificationService } from '@/services/notification'


export default /*@__PURE__*/_defineComponent({
  __name: 'Settings',
  setup(__props) {

const settingsView = ref(new SettingsView())
const email = ref<string | null>(null)
const isNativeApp = ref(false)

const popupChangePassword = ref<InstanceType<
  typeof ChangePasswordPopup
> | null>(null)

const popupChangeEmail = ref<InstanceType<typeof ChangeEmailPopup> | null>(null)

const notificationOptions = {
  newRelease: {
    title: 'New announcements',
    subtitle:
      'Updates about new summaries and content selected especially for you',
  },
  returnToValue: {
    title: 'Daily practice',
    subtitle: "We'll send reminders at a time that works best for you",
  },
}

async function updateNotifyNewRelease(value: boolean): Promise<void> {
  await settingsView.value.toggleNotificationSetting(
    'notify_new_release',
    value,
  )
}

async function updateNotifyReturnToValue(value: boolean): Promise<void> {
  await settingsView.value.toggleNotificationSetting(
    'notify_return_to_value',
    value,
  )
}

/**
 * Request notifications permission if it hasn't been granted.
 */
async function requestNotificationsIfDisabled(
  event: PointerEvent,
): Promise<void> {
  if (settingsView.value.isDeviceNotificationsEnabled) {
    return
  }

  event.preventDefault()

  if (!isNativeApp.value) {
    return
  }

  const notificationsPermission = await notificationService.checkPermission()
  if (notificationsPermission === 'denied') {
    // If the user explicitly denied notifications, we can't
    // request them again, so we open the notification settings
    // page, so they can manually enable them.
    await openNativeAppSettings()
  } else {
    // Otherwise, we request notifications permission.
    await notificationService.init(true)
    await settingsView.value.updateNotificationPermissions()
  }
}

async function onAppResume(appState: AppState): Promise<void> {
  if (appState.isActive) {
    // Update the notification permission state in the view after
    // the user comes back to the app (presumably after going to
    // the device's settings page).
    await settingsView.value.updateNotificationPermissions()
  }
}

function showChangePasswordPopup(): void {
  if (!popupChangePassword.value) {
    return
  }

  popupChangePassword.value.show()
}

function showChangeEmailPopup(): void {
  if (!popupChangeEmail.value) {
    return
  }

  popupChangeEmail.value.show()
}

function recheckEmail(): void {
  // The popupChangeEmail dialog has closed.
  // Re-check the email address in case it was successfully changed.
  email.value = getEmail()
}

onBeforeMount(async () => {
  isNativeApp.value = await platform.isNativeApp()
  await settingsView.value.init()
  email.value = getEmail()
  emitter.on('appVisibilityChange', onAppResume)
})

onBeforeUnmount(() => {
  emitter.off('appVisibilityChange', onAppResume)
})

return (_ctx: any,_cache: any) => {
  const _component_LoadingError = _resolveComponent("LoadingError")!

  return (_openBlock(), _createBlock(RootPageLayout, { title: "Settings" }, {
    default: _withCtx(() => [
      _createElementVNode("section", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (settingsView.value.isLoading)
            ? (_openBlock(), _createBlock(LoadingSpinner, { key: 0 }))
            : _createCommentVNode("", true),
          (settingsView.value.isError)
            ? (_openBlock(), _createBlock(_component_LoadingError, { key: 1 }))
            : _createCommentVNode("", true),
          (settingsView.value.isLoaded)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _cache[6] || (_cache[6] = _createElementVNode("ul", { class: "sidebar" }, [
                  _createElementVNode("li", { class: "sidebar__item" }, [
                    _createElementVNode("div", { class: "active-bar" }),
                    _createTextVNode(" Account "),
                    _createElementVNode("span", { class: "spacer" }),
                    _createElementVNode("span", { class: "iconfont iarrow active-arrow" })
                  ])
                ], -1)),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("section", _hoisted_5, [
                    _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "settings__section-title" }, "Device Notifications", -1)),
                    _cache[2] || (_cache[2] = _createElementVNode("p", { class: "settings__section-subtitle" }, " Control notifications on devices where you use Shortform ", -1)),
                    (
                  !settingsView.value.isDeviceNotificationsEnabled && !isNativeApp.value
                )
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[0] || (_cache[0] = [
                          _createElementVNode("h4", { class: "notifications-disabled__title" }, " Turn on browser notifications ", -1),
                          _createElementVNode("p", { class: "notifications-disabled__text" }, " Receive reminders that fit your schedule and help you keep learning consistently ", -1)
                        ]))]))
                      : _createCommentVNode("", true),
                    _createElementVNode("ul", _hoisted_7, [
                      _createElementVNode("li", _hoisted_8, [
                        _createVNode(ToggleButton, {
                          class: "settings__toggle",
                          disabled: !settingsView.value.isDeviceNotificationsEnabled,
                          value: 
                      settingsView.value.notificationSettings?.notify_new_release ||
                      false
                    ,
                          "onUpdate:value": updateNotifyNewRelease,
                          onClick: requestNotificationsIfDisabled
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("p", _hoisted_9, _toDisplayString(notificationOptions.newRelease.title), 1),
                            (notificationOptions.newRelease.subtitle)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(notificationOptions.newRelease.subtitle), 1))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }, 8, ["disabled", "value"])
                      ]),
                      (_unref(returnToValue19051Variant)() === 'B')
                        ? (_openBlock(), _createElementBlock("li", _hoisted_11, [
                            _createVNode(ToggleButton, {
                              class: "settings__toggle",
                              disabled: !settingsView.value.isDeviceNotificationsEnabled,
                              value: 
                      settingsView.value.notificationSettings
                        ?.notify_return_to_value || false
                    ,
                              "onUpdate:value": updateNotifyReturnToValue,
                              onClick: requestNotificationsIfDisabled
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("p", _hoisted_12, _toDisplayString(notificationOptions.returnToValue.title), 1),
                                _createElementVNode("p", _hoisted_13, _toDisplayString(notificationOptions.returnToValue.subtitle), 1)
                              ]),
                              _: 1
                            }, 8, ["disabled", "value"])
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _createElementVNode("section", _hoisted_14, [
                    _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "settings__section-title settings-email" }, " Account Email ", -1)),
                    _createElementVNode("p", _hoisted_15, _toDisplayString(email.value), 1),
                    _createVNode(AppButton, {
                      class: "update-email__btn btns btns--yellow",
                      onClick: showChangeEmailPopup
                    }, {
                      default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createTextVNode(" Change email ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(AppButton, {
                      class: "update-password__btn btns btns--yellow",
                      onClick: showChangePasswordPopup
                    }, {
                      default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                        _createTextVNode(" Change password ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(ChangePasswordPopup, {
                      ref_key: "popupChangePassword",
                      ref: popupChangePassword
                    }, null, 512),
                    _createVNode(ChangeEmailPopup, {
                      ref_key: "popupChangeEmail",
                      ref: popupChangeEmail,
                      onHide: recheckEmail
                    }, null, 512),
                    (popupChangePassword.value?.isSuccess())
                      ? (_openBlock(), _createElementBlock("p", _hoisted_16, " Password changed. "))
                      : _createCommentVNode("", true),
                    (popupChangeEmail.value?.isSuccess())
                      ? (_openBlock(), _createElementBlock("p", _hoisted_17, " Email address changed. "))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("section", _hoisted_18, [
                    _createVNode(InfoDeleteAccount)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})